import type React from 'react';

import type { InputSize } from '@/components/ui/input';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { cn } from '@/lib/utils';
import { FormFieldLabel } from './form-field-label';
import { FormFieldMessages } from './form-field-messages';
import type { BaseFormFieldProps } from './shared';
import { useFormField } from './shared';

export interface TextInputProps
  extends Omit<React.ComponentProps<'input'>, 'size'>,
    BaseFormFieldProps {
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  size?: InputSize;
}

function TextInput({
  className,
  type,
  label,
  helperText,
  helperTextPlacement = 'beforeControl',
  id,
  isRequired,
  showAsterisk,
  leftSection,
  rightSection,
  errorText,
  invalid,
  size,
  ref,
  ...props
}: TextInputProps) {
  const { fieldId, helperId, errorId, describedBy } = useFormField(id, helperText, errorText);
  const isInvalid = invalid || Boolean(errorText);
  const InputComponent = leftSection || rightSection ? InputGroupInput : Input;

  const inputElement = (
    <InputComponent
      aria-required={isRequired}
      aria-describedby={describedBy}
      aria-invalid={isInvalid || undefined}
      type={type}
      id={fieldId}
      ref={ref}
      size={size}
      {...props}
    />
  );

  return (
    <div className={cn('grid w-full gap-1.5', className)}>
      {label && (
        <FormFieldLabel
          htmlFor={fieldId}
          label={label}
          isRequired={isRequired}
          showAsterisk={showAsterisk}
        />
      )}
      {helperText && helperTextPlacement === 'beforeControl' && (
        <FormFieldMessages helperText={helperText} helperId={helperId} />
      )}
      {leftSection || rightSection ? (
        <InputGroup size={size}>
          {leftSection && <InputGroupAddon align="inline-start">{leftSection}</InputGroupAddon>}
          {inputElement}
          {rightSection && <InputGroupAddon align="inline-end">{rightSection}</InputGroupAddon>}
        </InputGroup>
      ) : (
        inputElement
      )}
      {helperText && helperTextPlacement === 'afterControl' && (
        <FormFieldMessages helperText={helperText} helperId={helperId} />
      )}
      {errorText && <FormFieldMessages errorText={errorText} errorId={errorId} />}
    </div>
  );
}

TextInput.displayName = 'TextInput';

export { TextInput };
